"use client";

import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";

/**
 * Options for {@link useKeyboardNav}.
 *
 * The hook attaches a `keydown` listener to the element returned by
 * `containerRef.current`. The container is intentionally scoped — keyboard
 * navigation should only fire while the table itself has focus, so different
 * tables on the same page don't fight each other.
 */
export interface UseKeyboardNavOptions {
  /** Total number of rows currently rendered (post-filter, pre-pagination). */
  rowCount: number;
  /** Called with the current focused row index when Enter is pressed. */
  onActivate?: (index: number) => void;
  /** Called with the current focused row index when Space is pressed. */
  onToggleSelect?: (index: number) => void;
  /** Called when Cmd/Ctrl + A is pressed. */
  onSelectAll?: () => void;
  /** The element the keydown listener attaches to. */
  containerRef: RefObject<HTMLElement | null>;
}

/** Return shape of {@link useKeyboardNav}. */
export interface UseKeyboardNavResult {
  /** Index of the row that currently has navigational focus. */
  focusedRowIndex: number;
  /** Imperative setter — useful when row clicks should sync focus. */
  setFocusedRowIndex: (index: number) => void;
}

/**
 * Returns true if the keydown originated from a control where the user is
 * editing text (input, textarea, select, contentEditable). Mirrors the rule
 * in `@aio/platform-keyboard`'s `useHotkey` — kept local here so this package
 * stays independent of the keyboard registry.
 */
function shouldIgnore(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Vim-style row navigation for table-shaped surfaces.
 *
 * Bindings:
 *   - ArrowDown / `j` — move focus to the next row (clamped to `rowCount - 1`)
 *   - ArrowUp / `k` — move focus to the previous row (clamped to 0)
 *   - Enter — call `onActivate(focusedRowIndex)`
 *   - Space — call `onToggleSelect(focusedRowIndex)`
 *   - Cmd/Ctrl + A — call `onSelectAll()`
 *
 * The listener attaches to `containerRef.current`. Events from inputs,
 * textareas, selects, or contentEditable elements *inside* the container are
 * ignored so editing a cell-level filter doesn't move row focus.
 */
export function useKeyboardNav(
  options: UseKeyboardNavOptions,
): UseKeyboardNavResult {
  const [focusedRowIndex, setFocusedRowIndex] = useState(0);

  // Mirror the latest props into a ref so the listener (registered once) sees
  // current handlers and rowCount without needing to re-bind on every render.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Same trick for the focused index — Enter / Space need the *current* value
  // when the event fires, not whatever was captured when the listener was
  // attached.
  const focusedRowIndexRef = useRef(focusedRowIndex);
  useEffect(() => {
    focusedRowIndexRef.current = focusedRowIndex;
  }, [focusedRowIndex]);

  const { containerRef } = options;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onKey = (e: KeyboardEvent) => {
      if (shouldIgnore(e.target)) return;

      const o = optionsRef.current;
      const cmdPressed = e.metaKey || e.ctrlKey;
      const max = Math.max(0, o.rowCount - 1);

      // Cmd/Ctrl + A — select all. Check before single-letter handlers so
      // 'a' with the modifier doesn't fall through to nothing.
      if (cmdPressed && e.key.toLowerCase() === "a") {
        e.preventDefault();
        o.onSelectAll?.();
        return;
      }

      if (e.key === "ArrowDown" || e.key === "j" || e.key === "J") {
        e.preventDefault();
        setFocusedRowIndex((i) => Math.min(max, i + 1));
        return;
      }

      if (e.key === "ArrowUp" || e.key === "k" || e.key === "K") {
        e.preventDefault();
        setFocusedRowIndex((i) => Math.max(0, i - 1));
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        o.onActivate?.(focusedRowIndexRef.current);
        return;
      }

      if (e.key === " ") {
        e.preventDefault();
        o.onToggleSelect?.(focusedRowIndexRef.current);
        return;
      }
    };

    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [containerRef]);

  return { focusedRowIndex, setFocusedRowIndex };
}
